import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { combineLatest, EMPTY, of } from 'rxjs';
import { debounceTime, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import type { Action } from 'state/actions';
import type { DashboardLoaded } from 'state/quotes/dashboard/dashboard.actions';
import { dashboardPersistenceRequested } from 'state/quotes/dashboard/dashboard.actions';
import type { EpicWithThunk, Thunk } from 'state/state.common';
import { logFrontError } from 'utils/errors/logFrontError';

export function triggerSaveActiveQuotes(scheduler?: SchedulerLike): EpicWithThunk {
  return (
    actions$,
    $state,
    {
      selectors: {
        dashboard: { buildDashboardPersistenceRequest },
      },
    },
  ) => {
    const loadSucceed$: Observable<DashboardLoaded> = actions$.pipe(ofType('DASHBOARD_LOADED'));
    const saveRequest$: Observable<Action | Thunk<void>> = actions$.pipe(
      ofType(
        'DASHBOARD_TILE_CREATED',
        'DASHBOARD_TILE_DUPLICATED',
        'QUOTE_RECEIVED',
        'QUOTES_REMOVED',

        'DASHBOARD_TILE_PIN_TOGGLED',
        'DASHBOARD_TILE_REMOVED',
        'DASHBOARD_TILE_GROUP_CHANGED',
        'DASHBOARD_PINNED_GROUP_RENAMED',
        'DASHBOARD_PINNED_GROUP_COLOR_CHANGED',
        'DASHBOARD_OBSOLETE_QUOTES_DELETED',

        'TRADE_AUTHORIZATION_RECEIVED',
        'TRADE_AUTHORIZATION_TIMEOUT',

        'KID_DOCUMENT_RECEIVED',
        'KID_DOCUMENT_TIMEOUT',

        'TERMSHEET_DOCUMENT_RECEIVED',
        'TERMSHEET_DOCUMENT_TIMEOUT',

        'PRODUCT_GOVERNANCE_ASSESSMENT_RECEIVED',
        'PRODUCT_GOVERNANCE_ASSESSMENT_TIMEOUT',

        'PRODUCT_GOVERNANCE_DOCUMENT_RECEIVED',
        'PRODUCT_GOVERNANCE_DOCUMENT_TIMEOUT',
      ),
      withLatestFrom($state),
      debounceTime(500, scheduler), // Debounce concurrent actions (exemple: for several tiles deletion)
      mergeMap(([_, state]) => {
        try {
          const request = buildDashboardPersistenceRequest(state);
          return of(dashboardPersistenceRequested(request));
        } catch (err) {
          logFrontError('Crash when trying to build save dashboard request: {@FrontError}', err);
          return EMPTY;
        }
      }),
    );

    return combineLatest([saveRequest$, loadSucceed$]).pipe(map(([save, _load]) => save));
  };
}
